// Small shared building blocks reused across every Accounting page, kept
// consistent with the rest of the Society panel's design system (same
// border/color/spacing tokens as HousingSocietyPanel.jsx).
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import { buttonVariants } from "../../../../components/ui/button";

/**
 * PHASE 19 - ACCOUNTING CONTROLS & AUDIT. Reads the `accountingPermissions`
 * array the backend now computes server-side and returns on every
 * /auth/me / login response (see _society_accounting_permission_summary in
 * server.py) - the frontend never re-derives the role/societyRole logic
 * itself, so there is exactly one place (the backend) that can ever be
 * wrong about who can do what. Hiding a menu/button here is a UX
 * convenience only; the backend enforces every one of these the same way
 * regardless of what the UI shows (see _require_society_permission calls).
 */
export function useAccountingPermissions() {
  const { user } = useAuth();
  const granted = new Set(user?.accountingPermissions || []);
  return {
    canView: granted.has("accounting.view"),
    canCreate: granted.has("accounting.create"),
    canEdit: granted.has("accounting.edit"),
    canDelete: granted.has("accounting.delete"),
    canPrint: granted.has("accounting.print"),
    canReconcile: granted.has("bank.reconcile"),
    canCloseFY: granted.has("accounting.fyclose"),
    canManageMasters: granted.has("accounting.masters"),
    canManageSettings: granted.has("accounting.settings"),
    has: (permission) => granted.has(permission),
  };
}

export function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.16em] text-[#6B7280]">{label}</Label>
      {children}
    </div>
  );
}

export function StatCard({ label, value }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <div className="text-2xl font-extrabold text-[#111827]">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-[0.16em] text-[#6B7280]">{label}</div>
    </div>
  );
}

export function SectionCard({ title, subtitle, actions, children, className = "" }) {
  return (
    <div className={`rounded-sm border border-[#E5E7EB] bg-white p-5 ${className}`}>
      {(title || actions) && (
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            {title && <h3 className="text-lg font-extrabold text-[#111827]">{title}</h3>}
            {subtitle && <div className="mt-1 text-sm text-[#6B7280]">{subtitle}</div>}
          </div>
          {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * Structural placeholder for report pages whose calculation engine has not
 * been built yet (later accounting phases). Explicitly allowed by the Phase 2
 * spec: pages without functionality yet should show a clean, professional
 * state rather than fake data or a blank screen.
 */
/**
 * Standard "Actions" column pattern for every Accounting master/list table
 * (Bank Accounts today; Ledgers/Vendors/Funds/Fixed Deposits/Financial
 * Years/Vouchers etc. in later phases per the cross-module View/Edit
 * requirement). Keeps every record-list page rendering the same pipe-
 * separated action links instead of each page inventing its own pattern.
 *
 * Usage:
 *   <ActionsCell>
 *     <ActionLink to={`${ACCOUNTING_BASE}/bank-accounts/${row.id}`}>View</ActionLink>
 *     <ActionLink to={`${ACCOUNTING_BASE}/bank-accounts/${row.id}/edit`}>Edit</ActionLink>
 *     <ActionLink tone="muted" disabled title="Ledger has posted entries">Deactivate</ActionLink>
 *   </ActionsCell>
 */
export function ActionsCell({ children, align = "right" }) {
  return <div className={`flex flex-wrap items-center gap-2 text-xs font-semibold ${align === "right" ? "justify-end" : ""}`}>{children}</div>;
}

const actionTone = {
  default: "text-[#111827] hover:underline",
  muted: "text-[#9CA3AF] cursor-not-allowed",
  danger: "text-red-600 hover:underline",
};

export function ActionLink({ to, onClick, tone = "default", disabled = false, title, children }) {
  const className = `${actionTone[disabled ? "muted" : tone]}`;
  if (disabled || !to) {
    return <span className={className} title={title}>{children}</span>;
  }
  if (onClick) {
    return <button type="button" className={className} title={title} onClick={onClick}>{children}</button>;
  }
  return <Link to={to} className={className} title={title}>{children}</Link>;
}

/** Small labelled value used on every View/detail page (bank account,
 * ledger, financial year, etc.) to keep detail-page layout consistent. */
export function DetailField({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-[#111827]">{value === "" || value === null || value === undefined ? "-" : value}</div>
    </div>
  );
}

/** Consistent Active/Inactive, Yes/No style status pill for detail pages
 * and Actions-adjacent status columns. */
export function StatusPill({ active, trueLabel = "Active", falseLabel = "Inactive" }) {
  return (
    <span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${active ? "bg-emerald-50 text-emerald-700" : "bg-[#F3F4F6] text-[#6B7280]"}`}>
      {active ? trueLabel : falseLabel}
    </span>
  );
}

/** Compact search box used at the top of every Phase 4 master list page
 * (Ledgers, Vendors, Funds, Fixed Deposits) so list-first pages share one
 * search interaction instead of each page inventing its own. Filtering
 * itself happens client-side in the page (society-scale master lists are
 * small; see the Phase 4 completion report for why server-side pagination
 * was judged unnecessary here). */
export function SearchBox({ value, onChange, placeholder = "Search..." }) {
  return (
    <div className="w-full max-w-xs">
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

export function EmptyState({ children }) {
  return <div className="rounded-sm border border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-8 text-center text-sm text-[#6B7280]">{children}</div>;
}

/**
 * Shared destructive-delete confirmation dialog for every master's "Delete"
 * action (Ledger, Vendor, Fund, Fixed Deposit - Bank Account keeps its own
 * copy from the earlier lifecycle correction). Mirrors the exact wording
 * pattern required for Bank Account delete: identify the record, state that
 * it has no history and can be safely removed, and make the irreversibility
 * explicit before the destructive button is enabled.
 */
export function ConfirmDeleteDialog({ open, onOpenChange, title, identity, safeMessage, busy, onConfirm, confirmLabel = "Delete permanently" }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-[#374151]">
              {identity && <div className="font-semibold text-[#111827]">{identity}</div>}
              <p>{safeMessage}</p>
              <p className="font-semibold text-red-600">This action cannot be undone.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={busy} onClick={onConfirm} className={buttonVariants({ variant: "destructive" })}>
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Phase 9: shared confirmation-with-required-reason dialog for every
 * sensitive action the spec calls out (voucher cancellation, reconciliation
 * reopen, period unlock, Financial Year status/reopen change, auditor
 * access changes) - deliberately separate from ConfirmDeleteDialog above
 * (which is for irreversible master deletes with no reason) so ordinary
 * reads/navigation never get an accidental confirmation prompt. The backend
 * for every one of these actions already requires reason.length >= 3; this
 * dialog enforces the same minimum client-side so the request never round-
 * trips just to bounce back with a 400.
 */
export function ReasonDialog({ open, onOpenChange, title, description, busy, onConfirm, confirmLabel = "Confirm", minLength = 3, placeholder = "Reason (required)..." }) {
  const [reason, setReason] = useState("");
  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) setReason(""); onOpenChange(v); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-[#374151]">
              {description && <p>{description}</p>}
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={placeholder} rows={3} />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy || reason.trim().length < minLength}
            onClick={() => onConfirm(reason.trim())}
            className={buttonVariants({ variant: "default" })}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ComingSoon({ title, note }) {
  return (
    <div className="rounded-sm border border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-10 text-center">
      <div className="text-sm font-extrabold uppercase tracking-[0.16em] text-[#6B7280]">{title}</div>
      <p className="mx-auto mt-2 max-w-md text-sm text-[#6B7280]">
        {note || "This report will become available once the accounting engine and reporting layer are implemented in a later phase."}
      </p>
    </div>
  );
}
